/**
 * HeroTwinBridge.jsx — GRANULLO_PAYMENT_TWIN_DOCTRINE_V3 (world-class)
 *
 * Goal: make the Payment Twin concept instantly understandable, visually alive,
 * and obviously groundbreaking. Three scenes, not three weak tabs.
 *
 *   Scene 1 — BEFORE: money vanishes into a PDF 3 months later
 *   Scene 2 — NOW:    every verified payment spawns a Twin (structured memory card)
 *   Scene 3 — FUTURE: next payment is judged by all the twins before it
 *
 * Anatomy panel reveals the 6 fields that make a Twin. Pillar strip explains
 * why this is structurally different.
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Eye, GitBranch, Zap, Clock, AlertTriangle, CheckCircle2,
  FileText, Fingerprint, Sparkles, Infinity as InfinityIcon, Database, BrainCircuit, ArrowRight,
} from 'lucide-react';

const RED = '#CD2200';
const AMBER = '#D97706';
const GREEN = '#10B981';
const MUTE = '#6B7280';

/* ═══ TWIN ANATOMY — 6 fields that make a Twin ═══ */
const TWIN_FIELDS = [
  { key: 'identity', label: 'Identity',  desc: 'Who sent. Who received. What for.',                     icon: Fingerprint, color: '#6B7280', sample: 'farmer#48213 · seeds' },
  { key: 'decision', label: 'Decision',  desc: 'Which rule approved. Which rule blocked.',              icon: GitBranch,   color: '#7C3AED', sample: 'rule #R-04 approved' },
  { key: 'proofs',   label: 'Proofs',    desc: 'Documentary evidence frozen at execution time.',        icon: FileText,    color: '#2563EB', sample: 'BL + COO + AIS' },
  { key: 'outcome',  label: 'Outcome',   desc: 'What actually happened downstream.',                    icon: CheckCircle2, color: '#10B981', sample: 'arrived · 14 days' },
  { key: 'prevent',  label: 'Prevented', desc: 'The misuse this payment did NOT cause.',                icon: Shield,      color: '#CD2200', sample: 'no duplicate, no ghost' },
  { key: 'learn',    label: 'Learning',  desc: 'What the next similar payment inherits from this one.', icon: BrainCircuit, color: '#0EA5E9', sample: '+1 similar · season drift' },
];

/* ═══ GROUNDBREAKING PILLARS ═══ */
const PILLARS = [
  { icon: InfinityIcon, title: 'Permanent',   desc: 'Twin never expires. The payment dies; the memory doesn\u2019t.' },
  { icon: Database,     title: 'Comparable',  desc: 'Every twin is structured the same way, so any two can be compared instantly.' },
  { icon: GitBranch,    title: 'Portable',    desc: 'The twin travels with the money. Not locked inside one org\u2019s system.' },
  { icon: BrainCircuit, title: 'Learning',    desc: 'Each twin teaches the next payment. The system gets smarter on its own.' },
];

/* ═══ SCENE 3 — PRIOR TWINS INFORMING A NEW DECISION ═══ */
const PRIOR_TWINS = [
  { year: '2022 · Q3', label: 'Seeds · Rajasthan', result: 'arrived',    color: GREEN },
  { year: '2023 · Q1', label: 'Seeds · Rajasthan', result: 'delayed',    color: AMBER },
  { year: '2023 · Q3', label: 'Seeds · Turkana',   result: 'blocked',    color: RED },
  { year: '2024 · Q2', label: 'Seeds · Rajasthan', result: 'arrived',    color: GREEN },
];

const SCENES = [
  { id: 'before',  label: 'Before Granullo',  micro: '01 · reporting only',    icon: Clock },
  { id: 'born',    label: 'A Twin is born',    micro: '02 · at execution',      icon: Sparkles },
  { id: 'future',  label: 'Next payment learns', micro: '03 · compounding',     icon: BrainCircuit },
];

export default function HeroTwinBridge() {
  const [activeScene, setActiveScene] = useState(0);
  const [typeIdx, setTypeIdx] = useState(0);

  // Typewriter auto-fill when Scene 2 is active
  useEffect(() => {
    if (activeScene !== 1) { setTypeIdx(0); return; }
    setTypeIdx(0);
    const iv = setInterval(() => {
      setTypeIdx((i) => {
        if (i >= TWIN_FIELDS.length) { clearInterval(iv); return i; }
        return i + 1;
      });
    }, 420);
    return () => clearInterval(iv);
  }, [activeScene]);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: 'var(--shell-bg, #050505)', padding: '90px 0 120px' }}
      data-testid="payment-twin-section"
    >
      {/* Ambient grid + grain */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(0deg, rgba(205,34,0,0.035) 0 1px, transparent 1px),
                          linear-gradient(90deg, rgba(205,34,0,0.035) 0 1px, transparent 1px)`,
        backgroundSize: '80px 80px',
        maskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
      }} />

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-8">

        {/* ═══ HERO COPY ═══ */}
        <motion.p
          initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          className="text-[10px] font-mono uppercase tracking-[0.26em] mb-4"
          style={{ color: RED }}
        >
          <Sparkles size={11} style={{ display: 'inline', verticalAlign: -2, marginRight: 6 }} />
          Payment Twin Doctrine
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          className="max-w-[960px] text-[32px] sm:text-[44px] lg:text-[56px] font-semibold leading-[1.05] tracking-[-0.025em]"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif', color: 'var(--shell-text)' }}
          data-testid="twin-headline"
        >
          Every verified payment now leaves behind a <span style={{ color: RED }}>Twin</span>.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.15 }}
          className="mt-4 max-w-[720px] text-[14px] sm:text-[16px] leading-[1.6]"
          style={{ color: 'var(--shell-text-secondary)' }}
        >
          A Twin is a permanent, structured memory of what a payment stopped, what it proved, what it achieved,
          and what the system learned.
          <br />
          <span className="inline-block mt-2" style={{ fontFamily: '"Kalam", cursive', fontSize: 22, color: RED }}>
            money dies. memory doesn&rsquo;t.
          </span>
        </motion.p>

        {/* ═══ SCENE SWITCHER ═══ */}
        <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="twin-scene-tabs">
          {SCENES.map((sc, i) => {
            const active = i === activeScene;
            const Ic = sc.icon;
            return (
              <motion.button
                key={sc.id}
                onClick={() => setActiveScene(i)}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full text-[12px] font-semibold cursor-pointer"
                style={{
                  background: active ? RED : 'var(--shell-surface)',
                  color: active ? '#fff' : 'var(--shell-text)',
                  border: `1px solid ${active ? RED : 'var(--shell-border)'}`,
                  boxShadow: active ? `0 0 24px ${RED}33` : 'none',
                  transition: 'all 220ms ease',
                }}
                data-testid={`twin-scene-${sc.id}`}
              >
                <Ic size={12} />
                {sc.label}
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] opacity-70">
                  · {sc.micro}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* ═══ ACTIVE SCENE STAGE ═══ */}
        <div
          className="mt-6 rounded-2xl overflow-hidden relative"
          style={{
            background: 'var(--shell-surface)',
            border: '1px solid var(--shell-border)',
            minHeight: 420,
          }}
        >
          <AnimatePresence mode="wait">
            {activeScene === 0 && <SceneBefore key="before" />}
            {activeScene === 1 && <SceneTwinBorn key="born" typeIdx={typeIdx} />}
            {activeScene === 2 && <SceneFutureLearns key="future" />}
          </AnimatePresence>
        </div>

        {/* ═══ WHY GROUNDBREAKING — PILLAR STRIP ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          className="mt-10"
          data-testid="twin-pillars"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.24em] mb-4" style={{ color: 'var(--shell-text-muted)' }}>
            Why it is structurally different
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {PILLARS.map((p, i) => {
              const Ic = p.icon;
              return (
                <motion.div
                  key={p.title}
                  whileHover={{ y: -2 }}
                  className="p-4 rounded-xl relative overflow-hidden"
                  style={{
                    background: 'var(--shell-surface)',
                    border: '1px solid var(--shell-border)',
                    transition: 'all 240ms ease',
                  }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: RED, opacity: 0.85 }} />
                  <Ic size={15} style={{ color: RED, marginBottom: 8 }} />
                  <p className="text-[14px] font-semibold" style={{ color: 'var(--shell-text)', letterSpacing: '-0.01em' }}>
                    {p.title}
                  </p>
                  <p className="text-[11.5px] leading-[1.5] mt-1" style={{ color: 'var(--shell-text-secondary)' }}>
                    {p.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ═══ CLOSING HANDWRITTEN ═══ */}
        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-10 text-center"
          style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(22px, 4vw, 32px)', color: RED }}
        >
          &ldquo;Every payment now teaches the next one.&rdquo;
        </motion.p>
      </div>
    </section>
  );
}

/* ═══════════════════════ SCENE 1 — BEFORE ═══════════════════════ */
function SceneBefore() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="grid lg:grid-cols-2 gap-6 p-6 sm:p-8"
      data-testid="scene-before"
    >
      {/* LEFT — payment happening */}
      <div className="flex flex-col gap-3">
        <p className="text-[9px] font-mono uppercase tracking-[0.24em]" style={{ color: 'var(--shell-text-muted)' }}>
          Payment executes
        </p>
        <TimelineRow icon="🏦" title="Transaction initiated" t="09:41" state="done" />
        <TimelineRow icon="💸" title="Money moved through rails" t="09:41.002" state="done" />
        <TimelineRow icon="📄" title="Reconciled across 4 systems" t="+ 6 hrs" state="done" muted />
        <TimelineRow icon="📦" title="Report filed" t="+ 3 months" state="late" />
        <div className="mt-2 flex items-center gap-2 text-[11px]" style={{ color: `${RED}CC` }}>
          <AlertTriangle size={12} />
          <span className="italic">By the time the report exists, the context is gone.</span>
        </div>
      </div>

      {/* RIGHT — the "memory" panel (empty/shredded) */}
      <div
        className="relative rounded-xl p-5 flex flex-col"
        style={{
          background: 'var(--shell-surface)',
          border: `1px dashed ${MUTE}55`,
          minHeight: 260,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: 'var(--shell-text-muted)' }}>
            What the system remembers
          </p>
          <span className="text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
            style={{ color: RED, background: `${RED}10`, border: `1px solid ${RED}35` }}>
            fragmented
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {['Ledger entry (bank)', 'PDF report (ops)', 'Spreadsheet (finance)', 'Email trail (audit)', 'Tribal knowledge (lost when staff leaves)'].map((line, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }} animate={{ opacity: 0.9 - i * 0.12 }}
              transition={{ delay: i * 0.08 }}
              className="flex items-center gap-2 text-[12px] px-3 py-2 rounded"
              style={{
                background: 'var(--shell-bg)',
                border: '1px solid var(--shell-border)',
                color: i === 4 ? 'var(--shell-text-muted)' : 'var(--shell-text-secondary)',
                textDecoration: i === 4 ? 'line-through' : 'none',
              }}
            >
              <span className="w-1 h-1 rounded-full" style={{ background: MUTE }} />
              {line}
            </motion.div>
          ))}
        </div>
        <p className="mt-auto pt-3 text-[12px] -rotate-1" style={{ fontFamily: '"Caveat", cursive', color: `${RED}CC` }}>
          &ldquo;what actually happened?&rdquo; &mdash; unresolved.
        </p>
      </div>
    </motion.div>
  );
}

function TimelineRow({ icon, title, t, state, muted }) {
  const color = state === 'late' ? RED : state === 'done' ? MUTE : MUTE;
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }} animate={{ opacity: muted ? 0.55 : 1, x: 0 }}
      transition={{ duration: 0.35 }}
      className="flex items-center gap-3 px-4 py-3 rounded-lg"
      style={{
        background: 'var(--shell-surface)',
        border: `1px solid ${state === 'late' ? `${RED}28` : 'var(--shell-border)'}`,
      }}
    >
      <span className="shrink-0 text-[16px]">{icon}</span>
      <span className="flex-1 text-[12.5px]" style={{ color: 'var(--shell-text)' }}>{title}</span>
      <span className="text-[10px] font-mono tabular-nums" style={{ color }}>{t}</span>
    </motion.div>
  );
}

/* ═══════════════════════ SCENE 2 — TWIN IS BORN ═══════════════════════ */
function SceneTwinBorn({ typeIdx }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="grid lg:grid-cols-[1fr_1.1fr] gap-6 p-6 sm:p-8"
      data-testid="scene-twin-born"
    >
      {/* LEFT — payment executes + twin emerges */}
      <div className="flex flex-col gap-4">
        <p className="text-[9px] font-mono uppercase tracking-[0.24em]" style={{ color: 'var(--shell-text-muted)' }}>
          At execution time
        </p>
        <motion.div
          initial={{ scale: 0.96, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
          className="rounded-xl p-4 flex items-center gap-3"
          style={{ background: 'var(--shell-surface)', border: `1px solid ${GREEN}35` }}
        >
          <CheckCircle2 size={18} style={{ color: GREEN }} />
          <div className="flex-1">
            <p className="text-[12px] font-semibold" style={{ color: 'var(--shell-text)' }}>
              Payment verified & released
            </p>
            <p className="text-[10px] font-mono" style={{ color: 'var(--shell-text-muted)' }}>
              $4,200 · seeds · farmer #48213 · Rajasthan
            </p>
          </div>
          <span className="text-[9px] font-mono uppercase tracking-[0.14em]"
            style={{ color: GREEN, background: `${GREEN}12`, border: `1px solid ${GREEN}35`, padding: '2px 6px', borderRadius: 4 }}>
            ✓ released
          </span>
        </motion.div>

        {/* down-arrow → twin emerges */}
        <div className="flex justify-center">
          <motion.span
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-[11px] font-mono uppercase tracking-[0.2em]"
            style={{ color: RED }}
          >
            ↓ twin emerges
          </motion.span>
        </div>

        {/* handwritten explanation */}
        <p className="text-[13px] leading-[1.6]" style={{ color: 'var(--shell-text-secondary)' }}>
          The moment the payment settles, Granullo captures a structured record of
          <strong style={{ color: 'var(--shell-text)' }}> what happened, what was prevented, and what the system just learned</strong>.
          That record is the <strong style={{ color: RED }}>Twin</strong>.
        </p>
        <p className="text-[13px] leading-[1.6]" style={{ color: 'var(--shell-text-secondary)' }}>
          The payment can vanish tomorrow. The Twin cannot.
        </p>
      </div>

      {/* RIGHT — Twin card being filled in */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="relative rounded-xl p-5"
        style={{
          background: `linear-gradient(180deg, ${RED}0A, var(--shell-surface))`,
          border: `1px solid ${RED}44`,
          boxShadow: `0 20px 48px ${RED}18, 0 0 0 1px ${RED}18 inset, 0 1px 0 rgba(255,255,255,0.6) inset`,
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${RED}14`, border: `1px solid ${RED}55` }}>
              <Sparkles size={11} style={{ color: RED }} />
            </div>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] font-semibold" style={{ color: 'var(--shell-text)' }}>
              Payment Twin · TX-48213-Q2
            </p>
          </div>
          <span className="text-[9px] font-mono font-semibold uppercase tracking-[0.16em]"
            style={{ color: GREEN, background: `${GREEN}14`, border: `1px solid ${GREEN}40`, padding: '3px 7px', borderRadius: 4 }}>
            ● writing
          </span>
        </div>

        <div className="flex flex-col gap-2.5">
          {TWIN_FIELDS.map((f, i) => {
            const filled = i < typeIdx;
            const Ic = f.icon;
            return (
              <motion.div
                key={f.key}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: filled ? 1 : 0.35, x: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-[96px_1fr_auto] items-center gap-3 px-3 py-2 rounded-lg"
                style={{
                  background: filled ? 'var(--shell-bg)' : 'transparent',
                  border: `1px solid ${filled ? `${f.color}38` : 'var(--shell-border)'}`,
                  transition: 'all 300ms ease',
                }}
                data-lens-id={`twin-field-${f.key}`}
                data-lens-type="payment-twin"
                data-lens-title={`Twin · ${f.label}`}
                data-lens-meaning={f.desc}
                data-lens-actions={`Inspect twin lattice|Compare prior twins|Replay outcome`}
                data-lens-state={filled ? 'sealed' : 'pending'}
              >
                <div className="flex items-center gap-2">
                  <Ic size={12} style={{ color: filled ? f.color : MUTE }} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em]"
                    style={{ color: filled ? f.color : MUTE }}>
                    {f.label}
                  </span>
                </div>
                <p className="text-[11.5px]" style={{ color: filled ? 'var(--shell-text)' : 'var(--shell-text-muted)' }}>
                  {f.desc}
                </p>
                <span className="text-[10px] font-mono tabular-nums"
                  style={{ color: filled ? f.color : 'transparent' }}>
                  {filled ? f.sample : '...'}
                </span>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 flex items-center justify-between" style={{ borderTop: '1px dashed var(--shell-border)' }}>
          <span className="text-[9px] font-mono uppercase tracking-[0.16em]" style={{ color: 'var(--shell-text-muted)' }}>
            hash · b3f8…a1
          </span>
          <span className="text-[9px] font-mono" style={{ color: GREEN }}>
            {typeIdx >= TWIN_FIELDS.length ? '✓ sealed · immortal' : `writing field ${typeIdx} / ${TWIN_FIELDS.length}`}
          </span>
        </div>

        {/* ═══ TWIN DOCTRINE STRIP — Future Risk · Structural Fix · Safe Payment · Why ═══ */}
        <AnimatePresence>
          {typeIdx >= TWIN_FIELDS.length && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="mt-4 rounded-lg p-3.5 grid grid-cols-2 gap-y-2.5 gap-x-4"
              style={{
                background: `${RED}05`,
                border: `1px solid ${RED}25`,
              }}
              data-testid="twin-doctrine-strip"
            >
              <DoctrineCell label="Future risk"    value="Tax leakage / withholding failure" color={RED} />
              <DoctrineCell label="Structural fix" value="13% automatically reserved before release" color={'var(--shell-text)'} />
              <DoctrineCell label="Safe payment"   value="Executed via banking rail after obligation separation" color={GREEN} />
              <DoctrineCell label="Why"            value="Money did not move incorrectly." color={GREEN} italic />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════════════ SCENE 3 — NEXT PAYMENT LEARNS ═══════════════════════ */
function SceneFutureLearns() {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="grid lg:grid-cols-[1.1fr_1fr] gap-6 p-6 sm:p-8"
      data-testid="scene-future"
    >
      {/* LEFT — new payment arriving, prior twins flowing in */}
      <div className="flex flex-col gap-3">
        <p className="text-[9px] font-mono uppercase tracking-[0.24em]" style={{ color: 'var(--shell-text-muted)' }}>
          A new payment arrives
        </p>

        <div
          className="rounded-xl p-4"
          style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${RED}14`, border: `1px solid ${RED}40` }}>
              <Zap size={12} style={{ color: RED }} />
            </div>
            <div className="flex-1">
              <p className="text-[12px] font-semibold" style={{ color: 'var(--shell-text)' }}>
                $4,600 · seeds · farmer #51892 · Rajasthan
              </p>
              <p className="text-[10px] font-mono" style={{ color: 'var(--shell-text-muted)' }}>
                awaiting decision · 2026-Q2
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-[9px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: 'var(--shell-text-muted)' }}>
              Prior twins pulled — same context
            </p>
            {PRIOR_TWINS.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className="grid grid-cols-[82px_1fr_auto] gap-3 items-center text-[11px] px-3 py-1.5 rounded"
                style={{ background: 'var(--shell-bg)', border: `1px solid ${t.color}35` }}
              >
                <span className="font-mono" style={{ color: 'var(--shell-text-muted)' }}>{t.year}</span>
                <span style={{ color: 'var(--shell-text)' }}>{t.label}</span>
                <span className="font-mono uppercase tracking-[0.14em] text-[9px] px-1.5 py-0.5 rounded"
                  style={{ color: t.color, background: `${t.color}15`, border: `1px solid ${t.color}40` }}>
                  {t.result}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — learning-driven decision */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="rounded-xl p-5 flex flex-col gap-3"
        style={{
          background: `linear-gradient(180deg, ${GREEN}0D, var(--shell-surface))`,
          border: `1px solid ${GREEN}40`,
          boxShadow: `0 20px 40px ${GREEN}18, 0 1px 0 rgba(255,255,255,0.6) inset`,
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <BrainCircuit size={14} style={{ color: GREEN }} />
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-semibold" style={{ color: GREEN }}>
            Granullo decides — informed by 4 twins
          </p>
        </div>

        <div className="space-y-2">
          <LearnPill accent={RED}   label="Drift detected" value="2023 Q1 delay pattern in monsoon" />
          <LearnPill accent={AMBER} label="Timing rule applied" value="release 14 days pre-season, not at-season" />
          <LearnPill accent={GREEN} label="Outcome forecast" value="96% arrival probability · $0 leakage" />
        </div>

        <div className="mt-2 px-4 py-3 rounded-lg flex items-center gap-3"
          style={{ background: `${GREEN}10`, border: `1px solid ${GREEN}50` }}
        >
          <CheckCircle2 size={16} style={{ color: GREEN }} />
          <div className="flex-1">
            <p className="text-[12px] font-semibold" style={{ color: 'var(--shell-text)' }}>
              Approved · released 14 days earlier than 2023
            </p>
            <p className="text-[10px] font-mono" style={{ color: 'var(--shell-text-muted)' }}>
              a new twin will be written when this one arrives
            </p>
          </div>
          <ArrowRight size={14} style={{ color: GREEN }} />
        </div>

        <p className="text-[13px] -rotate-1 mt-1" style={{ fontFamily: '"Caveat", cursive', color: GREEN }}>
          the 5th twin has learned from 1 through 4.
        </p>
      </motion.div>
    </motion.div>
  );
}

function LearnPill({ accent, label, value }) {
  return (
    <div className="flex items-baseline gap-3 px-3 py-2 rounded-md"
      style={{ background: 'var(--shell-bg)', border: `1px solid ${accent}35` }}>
      <span className="text-[9px] font-mono uppercase tracking-[0.16em] shrink-0"
        style={{ color: accent, minWidth: 120 }}>
        {label}
      </span>
      <span className="text-[11.5px] leading-tight" style={{ color: 'var(--shell-text)' }}>
        {value}
      </span>
    </div>
  );
}

/* Four-cell doctrine grammar: Future Risk · Structural Fix · Safe Payment · Why */
function DoctrineCell({ label, value, color, italic }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[9px] font-mono uppercase tracking-[0.18em]"
        style={{ color: 'var(--shell-text-muted)' }}>
        {label}
      </span>
      <span className="text-[11.5px] leading-[1.35]"
        style={{ color, fontStyle: italic ? 'italic' : 'normal' }}>
        {value}
      </span>
    </div>
  );
}
